/**
 * What a seat is trying to do in any discussion (debate/README.md §protocol 1). A seat without
 * a brief gets the library brief whose name matches its role, else a neutral one. Limits the
 * server enforces: each text field ≤ 400 characters, ≤ 12 interests, ≤ 6 challenges.
 */
export interface RoleBrief {
  name: string;
  intent: string;
  interests: string[];
  toward: string;
  challenges: string[];
  concedes: string;
  voice?: string | null;
  /** The role's field of interest (≤ 80 chars); the library is grouped by it. A custom brief may leave it empty. */
  category?: string | null;
}
/** The library, grouped by field of interest in library order; briefs without a field go last under "Other". */
export function briefsByCategory(
  library: RoleBrief[],
): { category: string; briefs: RoleBrief[] }[] {
  const groups = new Map<string, RoleBrief[]>();
  for (const brief of library) {
    const category = brief.category?.trim() || "Other";
    const list = groups.get(category);
    if (list) list.push(brief);
    else groups.set(category, [brief]);
  }
  const other = groups.get("Other");
  groups.delete("Other");
  const ordered = [...groups.entries()].map(([category, briefs]) => ({ category, briefs }));
  return other ? [...ordered, { category: "Other", briefs: other }] : ordered;
}
/**
 * A council: a named table of library roles around one kind of question (`GET research/councils`).
 * Picking one seats `roles` (each with its library brief) over the workspace's thinkers and fills
 * the question; `examples` are subjects the question is typically asked about.
 */
export interface CouncilPreset {
  id: string;
  name: string;
  category: string;
  summary: string;
  question: string;
  roles: string[];
  examples: string[];
  method: ResearchRequest["method"];
  rounds: number;
  toolGroups?: ResearchToolGroup[] | null;
}
/** The councils grouped by field, in library order. */
export function councilsByCategory(
  councils: CouncilPreset[],
): { category: string; councils: CouncilPreset[] }[] {
  const groups = new Map<string, CouncilPreset[]>();
  for (const council of councils) {
    const list = groups.get(council.category);
    if (list) list.push(council);
    else groups.set(council.category, [council]);
  }
  return [...groups.entries()].map(([category, list]) => ({ category, councils: list }));
}
/**
 * The seats a council starts with: its roles with their library briefs, dealt round-robin over the
 * thinkers available here (one thinker takes every role when it is alone — with facets it brings a
 * different mind to each). No thinkers ⇒ empty thinker ids, which the setup names as the gap.
 */
export function councilSeats(
  council: CouncilPreset,
  library: RoleBrief[],
  thinkerIds: string[],
): ResearchSeat[] {
  return council.roles.map((role, index) => {
    const brief = library.find((b) => b.name.toLowerCase() === role.toLowerCase());
    return {
      thinkerId: thinkerIds.length > 0 ? (thinkerIds[index % thinkerIds.length] ?? "") : "",
      role: brief?.name ?? role,
      brief: brief
        ? { ...brief, interests: [...brief.interests], challenges: [...brief.challenges] }
        : null,
    };
  });
}
export interface ResearchSeat {
  thinkerId: string;
  role: string;
  brief?: RoleBrief | null;
}
/** A question in the dialogue queue. `from === -1` is the OWNER (the human) asking. */
export interface DialogueQuestion {
  id: string;
  contributionId: string;
  from: number;
  to: number;
  text: string;
}
/** A thinker's situational persona blend, carried on `turn-started` and `contribution` events. */
export interface PersonaBlend {
  weights: { id: string; name: string; weight: number; shared: string[] }[];
}
export interface ResearchReasoningPolicy {
  instructions: string;
  proposal: string;
  challenge: string;
  synthesis: string;
  enableThinking: boolean;
  shareReasoning: boolean;
  contextCharacters: number;
  temperature: number;
  seed: number | null;
  stopOnRepetition: boolean;
  includeParentContributions: boolean;
}
/**
 * What a debate can be ABOUT: the server reads the thing (ACL-checked) into the experts' evidence.
 * `news-item` and `news-storyline` are MarketPulse's (an article by id; a story line by key).
 */
export type ResearchContextKind =
  | "document"
  | "collection"
  | "work-item"
  | "news-item"
  | "news-storyline";
export const RESEARCH_CONTEXT_KINDS: readonly ResearchContextKind[] = [
  "document",
  "collection",
  "work-item",
  "news-item",
  "news-storyline",
];
/**
 * `id` is the GUID — for a work item the item's id, never its key. A `news-storyline` has no
 * GUID: it carries `key` = `kind|anchor|episode` (the Storylines page's own id) and an empty `id`.
 */
export interface ResearchContext {
  kind: ResearchContextKind;
  id: string;
  key?: string | null;
}
/** The `context` search param of `/research`: `<kind>:<id>`. Anything else is no context. */
export function parseResearchContext(value: unknown): ResearchContext | undefined {
  if (typeof value !== "string") return undefined;
  const at = value.indexOf(":");
  if (at < 1) return undefined;
  const kind = value.slice(0, at);
  const id = value.slice(at + 1).trim();
  return id && (RESEARCH_CONTEXT_KINDS as readonly string[]).includes(kind)
    ? { kind: kind as ResearchContextKind, id }
    : undefined;
}
export function researchContextParam(context: ResearchContext): string {
  return `${context.kind}:${context.id}`;
}
/** The `q` search param of `/research` is capped here; the server caps the question at 12,000. */
export const RESEARCH_QUESTION_PARAM_LIMIT = 2000;
/**
 * The one link every "Debate this …" button navigates to:
 * `/research?context=<kind>:<id>&q=<url-encoded question>`.
 */
export function debateHref(context: ResearchContext, question: string): string {
  const q = question.trim().slice(0, RESEARCH_QUESTION_PARAM_LIMIT);
  return `/research?context=${researchContextParam(context)}&q=${encodeURIComponent(q)}`;
}
export interface ResearchRequest {
  dialogueSeed?: string;
  flow?: { slug: string; version: number; tokenBudget: number };
  /** The thing this debate is about; the server echoes it on the experiment. */
  context?: ResearchContext;
  reasoning?: ResearchReasoningPolicy;
  maxTurns?: number;
  title?: string;
  question: string;
  seats: ResearchSeat[];
  rounds: number;
  maxTokens: number;
  affectedParts: string[];
  workItemId?: string;
  parentExperimentId?: string;
  method: "debate" | "independent" | "paired-review" | "dialogue";
  access?: { workspace: boolean; neo4j: boolean; sites: boolean; query?: string };
  /** `each-round` stops after every round with status `paused`; `continueRounds` resumes. */
  humanReview?: "none" | "each-round";
  /** What the experts may look up (research-tools README §2.2). Omitted ⇒ every group, server defaults. */
  tools?: ResearchToolPolicy;
  /** The council preset this research was seated from (`CouncilPreset.id`), remembered so the pages can say so. */
  council?: string;
}
/** `/research?council=<id>&q=<question>` — what a "Start a council" link carries to the research scene. */
export function councilHref(councilId: string, question?: string): string {
  const q = question?.trim().slice(0, RESEARCH_QUESTION_PARAM_LIMIT);
  return `/research?council=${encodeURIComponent(councilId)}${q ? `&q=${encodeURIComponent(q)}` : ""}`;
}

/** Tool groups a research grants (research-tools README §3). */
export type ResearchToolGroup =
  | "web"
  | "workspace"
  | "knowledge"
  | "code"
  | "work"
  | "research"
  | "math"
  | "causality";
export const RESEARCH_TOOL_GROUPS: readonly {
  id: ResearchToolGroup;
  label: string;
  description: string;
}[] = [
  { id: "web", label: "Web", description: "Search the internet and read the top pages" },
  {
    id: "workspace",
    label: "Workspace",
    description: "Search and read this workspace's documents",
  },
  { id: "knowledge", label: "Knowledge", description: "The wisdom graph and the knowledge tree" },
  { id: "code", label: "Code", description: "Find and read files in connected repositories" },
  { id: "work", label: "Work", description: "Docit Work items: bugs, features, tasks" },
  {
    id: "research",
    label: "Past research",
    description: "Earlier researches' outcomes and decisions",
  },
  { id: "math", label: "Math", description: "Exact arithmetic and simple equations" },
  {
    id: "causality",
    label: "Causality",
    description: "What measured causality says about cause → effect",
  },
];
/** Server defaults (ResearchToolPolicy): 3 lookups a turn, 40 a run, 5 web pages a search. */
export const RESEARCH_TOOL_DEFAULTS = {
  maxCallsPerTurn: 3,
  maxCallsPerRun: 40,
  maxWebPages: 5,
} as const;
export interface ResearchToolPolicy {
  /** Omitted ⇒ every group. */
  groups?: ResearchToolGroup[];
  maxCallsPerTurn?: number;
  maxCallsPerRun?: number;
  maxWebPages?: number;
}
/** A page or in-app thing a tool read. Untrusted; excerpt ≤ 1,500 characters. */
export interface ResearchToolSource {
  url: string | null;
  path: string | null;
  title: string;
  fetchedAt: string;
  excerpt: string;
}
/** `tool-call` event data. participantId `host` = the host's own web gather (contributionId null). */
export interface ResearchToolCallData {
  round: number;
  contributionId: string | null;
  participantId: string;
  facet: string | null;
  callId: string;
  tool: string;
  group: ResearchToolGroup | null;
  input: unknown;
  why: string | null;
}
/** `tool-result` event data. `refused`: not_granted | over_budget | unknown_tool | not_available. */
export interface ResearchToolResultData extends Omit<ResearchToolCallData, "input" | "why"> {
  ok: boolean;
  summary: string;
  sources: ResearchToolSource[];
  error: string | null;
  refused: "not_granted" | "over_budget" | "unknown_tool" | "not_available" | null;
  budget: { used: number; max: number };
}
export interface ResearchEvidence {
  id: string;
  kind: string;
  title: string;
  text: string;
  url: string | null;
  basis: string;
}
export interface ResearchKnowledgeSnapshot {
  query: string;
  capturedAt: string;
  workspaceStatus: string;
  neo4jStatus: string;
  sitesStatus: string;
  sources: ResearchEvidence[];
  relationships: { source: string; target: string; relation: string }[];
  notices: string[];
}
export interface ResearchEvent {
  sequence?: number;
  kind: string;
  at: string;
  data: Record<string, unknown>;
}
export interface ResearchExperiment {
  flowRunId?: string | null;
  flowDefinition?: unknown;
  modelCalls?: number;
  id: string;
  /** `created` | `running` | `paused` | `completed` | `failed` | `cancelled` (plus flow-run states). */
  status: string;
  request: ResearchRequest;
  events: ResearchEvent[];
  createdAt: string;
  updatedAt: string;
  title?: string;
  outcomeSummary?: string;
  outcomeNotes?: string;
  notesVersion?: number;
  notesHistory?: { version: number; text: string; savedAt: string }[];
}
export interface InstalledModel {
  id: string;
  repoId: string;
  requestedRevision: string;
  status: string;
  error: string | null;
  downloadedBytes: number;
  metadata: {
    revision?: string;
    sizeBytes?: number;
    license?: string;
    modelType?: string;
    contextTokens?: number;
    supportsThinking?: boolean;
    quantization?: { bits?: number };
  } | null;
}
export interface ResearchModels {
  root: string;
  enabled: boolean;
  models: InstalledModel[];
  maxResidentModels: number;
  maxResidentWeightBytes: number;
  configuredModels: string[];
}
export interface ResearchFlowTrace {
  recovery?: { nodeId: string; reuse: string[] } | null;
  experiment: ResearchExperiment;
  state: Record<string, unknown> | null;
  sessions: { id: string; flowNodeId: string; state: string; revision: number }[];
  operations: {
    id: string;
    sessionId: string;
    status: string;
    error: string | null;
    thinkerId: string;
    evaluatorThinkerId: string | null;
    generatorPrompt: string | null;
    evaluatorPrompt: string | null;
    generator: Record<string, unknown> | null;
    evaluator: Record<string, unknown> | null;
  }[];
}
export interface ResearchApi {
  recover(id: string, recoveryId: string): Promise<ResearchExperiment>;
  flow(id: string): Promise<ResearchFlowTrace>;
  models(): Promise<ResearchModels>;
  download(id: string, repoId: string, revision?: string): Promise<InstalledModel>;
  cancelDownload(id: string): Promise<unknown>;
  bind(
    modelId: string,
    thinkerId: string,
    displayName: string,
    role: string,
  ): Promise<{ thinkerId: string }>;
  impact(request: ResearchRequest): Promise<Record<string, unknown>>;
  context(request: ResearchRequest): Promise<ResearchKnowledgeSnapshot>;
  list(options?: { offset?: number; query?: string }): Promise<
    {
      id: string;
      title?: string;
      status: string;
      requestJson: string;
      outcomeSummary?: string;
      createdAt: string;
    }[]
  >;
  saveNotes(id: string, text: string, version: number): Promise<ResearchExperiment>;
  create(id: string, request: ResearchRequest): Promise<ResearchExperiment>;
  /**
   * The experiment. With `after`, `GET …/{id}?after=N` answers the same shape with only the
   * events whose sequence is above N PLUS the live ones (the open `turn-started`, the
   * `synthesis` being written) — merge it into what you hold with `mergeExperiment`. A server
   * that ignores `after` returns everything; the merge is right either way.
   */
  get(id: string, options?: ResearchGetOptions): Promise<ResearchExperiment>;
  /**
   * Start the debate. With `background`, the server answers 202 at once and the work goes on
   * server-side; the client then polls `get` every second (honouring `signal`) until the status
   * leaves `running` and resolves with that final experiment. Without it, the old synchronous
   * request: one long POST that returns the finished experiment.
   */
  run(id: string, signal: AbortSignal, options?: ResearchRunOptions): Promise<ResearchExperiment>;
  /**
   * Stop a running debate server-side (`POST …/cancel`). The answer may still read `running`
   * for a moment; keep polling `get` until it changes — a `cancelled` event appears.
   */
  cancel(id: string): Promise<ResearchExperiment>;
  /**
   * Talk at the table (`POST …/say {text, to, rounds?}` → 202 `running`): the server queues
   * your words as the owner's question to everyone (`"all"`) or one seat (`"expert-N"`) AND
   * starts a round at once; the client polls `get` until the status leaves `running`. 400 when
   * the research has not run yet or the text exceeds 2,000 characters; 409 while running.
   */
  say(
    id: string,
    text: string,
    to: string,
    signal: AbortSignal,
    options?: { rounds?: number; onUpdate?: ResearchRunOptions["onUpdate"] },
  ): Promise<ResearchExperiment>;
  /** The role-brief library (`GET research/roles`), every brief with its field of interest. */
  roles(): Promise<RoleBrief[]>;
  /** The councils (`GET research/councils`): named tables of library roles around one kind of question. */
  councils(): Promise<CouncilPreset[]>;
  /**
   * The owner asks the participants. `to` is `"all"` or `"expert-N"` (1-based). Queued for the
   * next continue; the server refuses (409) while the research is `running`.
   */
  ask(id: string, text: string, to: string): Promise<ResearchExperiment>;
  /**
   * Run more rounds from `paused`, `completed` or `failed` — a long request like `run`, so it
   * takes the same AbortSignal. Named `continueRounds` because `continue` is a keyword.
   */
  continueRounds(
    id: string,
    input: { rounds?: number; steer?: string },
    signal: AbortSignal,
    options?: ResearchRunOptions,
  ): Promise<ResearchExperiment>;
}
export interface ResearchRunOptions {
  /** `?background=true`: 202 now, poll `get` until the status leaves `running`. */
  background?: boolean;
  /**
   * Called with the whole experiment (merged) after every poll of a background run, so a page
   * can render the debate as it streams without polling on its own.
   */
  onUpdate?: (experiment: ResearchExperiment) => void;
}
export interface ResearchGetOptions {
  /** Only the events after this sequence, plus the live ones. */
  after?: number;
}

/** The highest event sequence an experiment holds (0 when it has none). */
export function highestSequence(experiment: ResearchExperiment | null | undefined): number {
  let max = 0;
  for (const event of experiment?.events ?? [])
    if (typeof event.sequence === "number" && event.sequence > max) max = event.sequence;
  return max;
}

/**
 * Merge an incremental answer (`get` with `after`) into the experiment held so far: an event
 * whose sequence is already known REPLACES it (a live turn's text grows in place), a new one is
 * appended, order is by sequence; events without a sequence are kept as they come. Everything
 * else (status, outcome, notes) is the newer answer's. With nothing held, the answer is taken
 * whole — which is also what a server that ignores `after` makes this.
 */
export function mergeExperiment(
  previous: ResearchExperiment | null | undefined,
  incoming: ResearchExperiment,
): ResearchExperiment {
  if (!previous || previous.id !== incoming.id) return incoming;
  const bySequence = new Map<number, ResearchEvent>();
  const loose: ResearchEvent[] = [];
  for (const event of previous.events ?? []) {
    if (typeof event.sequence === "number") bySequence.set(event.sequence, event);
    else loose.push(event);
  }
  for (const event of incoming.events ?? []) {
    if (typeof event.sequence === "number") bySequence.set(event.sequence, event);
    else loose.push(event);
  }
  const events = [...bySequence.values()].sort(
    (a, b) => (a.sequence as number) - (b.sequence as number),
  );
  return { ...previous, ...incoming, events: [...events, ...loose] };
}
